import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export const api = axios.create({
  baseURL: API_BASE_URL + '/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Users
export interface UserRegistrationData {
  username: string;
  email: string;
  age?: number;
  sex?: string;
  education_level?: string;
  study_specialty?: string;
  employment_status?: string;
  work_sector?: string;
}

export interface RegistrationOptions {
  sex_options: { value: string; label: string }[];
  education_levels: { value: string; label: string }[];
  study_specialties: { value: string; label: string }[];
  employment_status: { value: string; label: string }[];
  work_sectors: { value: string; label: string }[];
}

export const getRegistrationOptions = () =>
  api.get<RegistrationOptions>('/users/options/registration');

export const createUser = (data: UserRegistrationData) =>
  api.post('/users', data);

export const getUser = (userId: number) =>
  api.get(`/users/${userId}`);

export const getUserByUsername = (username: string) =>
  api.get(`/users/by-username/${encodeURIComponent(username)}`);

export const getUserAttempts = (userId: number) =>
  api.get(`/users/${userId}/attempts`);

// Test
export const startTest = (userId: number, version?: string) =>
  api.post('/start-test', { user_id: userId, version: version || 'A' });

export const getNextQuestion = (sessionId: string) =>
  api.get(`/question/next?session_id=${sessionId}`);

export const completeTest = (sessionId: string) =>
  api.post(`/complete-test?session_id=${sessionId}`);

// Results
export const getResults = (attemptId: number) =>
  api.get(`/results/${attemptId}`);

export const getProfileAnalysis = (attemptId: number, forceRegenerate: boolean = false) =>
  api.get(`/results/${attemptId}/profile?force=${forceRegenerate}`);

// New endpoints for separated profile and recommendations
export const getProfileReport = (attemptId: number) =>
  api.get(`/results/${attemptId}/profile-report`);

export const getRecommendations = (attemptId: number, forceRegenerate: boolean = false) =>
  api.get(`/results/${attemptId}/recommendations?force=${forceRegenerate}`);

// Metadata
export const getVersionInfo = (version: string) =>
  api.get(`/versions/${version}/info`);

export const getSectionInstruction = (version: string, sectionId: string) =>
  api.get(`/versions/${version}/sections/${sectionId}/instruction`);

// Navigation
export const navigateQuestion = (sessionId: string, direction: 'back' | 'forward') => {
  return api.post('/navigate-question', {
    session_id: parseInt(sessionId),
    direction
  });
};

export const markInstructionShown = (sessionId: string, sectionId: string, timeSpent: number) => {
  return api.post(`/session/${sessionId}/instruction-shown`, {
    section_id: sectionId,
    time_spent_seconds: timeSpent
  });
};

export const savePreRuleTime = (sessionId: string, questionId: string, timeSpent: number) => {
  return api.post(`/session/${sessionId}/pre-rule-time`, null, {
    params: {
      question_id: questionId,
      time_spent_seconds: timeSpent
    }
  });
};

// Update submitAnswer to include time_spent_seconds
export const submitAnswer = (sessionId: string, questionId: string, answer: string, timeSpent: number) => {
  return api.post('/submit-answer', {
    session_id: sessionId,
    question_id: questionId,
    answer: answer,
    time_spent_seconds: timeSpent
  });
};

// Save distraction game data
export const saveDistractionData = (sessionId: string, questionId: string, timeSpent: number, moves: number) => {
  return api.post(`/session/${sessionId}/distraction-data`, {
    question_id: questionId,
    time_spent_seconds: timeSpent,
    moves: moves
  });
};